import React, { useRef, useState } from 'react';
import Webcam from 'react-webcam';
import CountdownTimer from '../shared/CountdownTimer';
import { captureWebcamImage } from '../../utils/imageUtils';

const CameraScreen = ({ onPhotoCapture, onBack, orientation = 'portrait' }) => {
  const webcamRef = useRef(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const [webcamError, setWebcamError] = useState(false);

  React.useEffect(() => {
    if (!webcamError) {
      const timer = setTimeout(() => {
        setShowCountdown(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [webcamError]);

  const handleCountdownComplete = async () => {
    const imageSrc = await captureWebcamImage(webcamRef);
    if (imageSrc) {
      onPhotoCapture(imageSrc);
    } else {
      setWebcamError(true);
      setShowCountdown(false);
    }
  };

  const handleUserMedia = () => setWebcamError(false);
  const handleUserMediaError = (error) => {
    console.error('Webcam error:', error);
    setWebcamError(true);
  };

  // Minimal constraints to prevent aggressive zoom on phones
  // Let the browser/camera choose natural resolution without forcing crop
  const videoConstraints = {
    facingMode: 'user'
  };

  return (
    <div className="h-screen relative overflow-hidden bg-gradient-to-b from-[#E4EFEA] via-[#D1DDD7] to-[#B8CFC4]">
      {/* Vertical spine */}
      <div className="forensic-spine" />

      {/* Background - darker, more lab-like */}
      <div className="forensic-grid-overlay opacity-30" />
      <div className="forensic-data-stream absolute inset-0 opacity-20" />

      {/* Circuit lines - more prominent */}
      <div className="absolute left-0 top-1/4 w-32 h-[2px] bg-gradient-to-r from-[#0e734b] to-transparent" />
      <div className="absolute left-0 top-1/3 w-20 h-[2px] bg-gradient-to-r from-[#0e734b] to-transparent" />
      <div className="absolute right-0 bottom-1/4 w-24 h-[2px] bg-gradient-to-l from-[#0e734b] to-transparent" />

      {/* Green blocks */}
      <div className="forensic-block forensic-block-2" />
      <div className="forensic-corner-tl" />

      {/* Main content */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Header */}
        <header className="px-8 pt-6 pb-4 pl-12">
          <div className="flex items-center gap-3">
            <div className="forensic-marker w-10 h-10 text-sm">
              <span className="animate-pulse">●</span>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#0e734b] font-semibold">Photo Capture</p>
              <h1 className="text-xl font-bold uppercase tracking-[0.15em] text-[#1A1A1A]">Position Yourself</h1>
            </div>
          </div>
        </header>

        {/* Header line */}
        <div className="h-[2px] mx-8 bg-gradient-to-r from-[#0e734b] via-[#0e734b] to-transparent" style={{ backgroundSize: '30% 100%', backgroundRepeat: 'no-repeat' }} />

        {/* Camera view */}
        <main className="flex-1 flex items-center justify-center p-6 min-h-0">
          {!webcamError ? (
            <div className="relative flex items-center justify-center w-full h-full">
              <div
                className="relative w-full h-full max-w-full max-h-full"
                style={{
                  aspectRatio: '2/3',
                  maxWidth: 'min(100%, calc((100vh - 10rem) * 2/3))',
                  maxHeight: 'calc(100vh - 10rem)'
                }}
              >
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  screenshotFormat="image/jpeg"
                  videoConstraints={videoConstraints}
                  onUserMedia={handleUserMedia}
                  onUserMediaError={handleUserMediaError}
                  className="absolute inset-0 w-full h-full object-cover rounded-sharper"
                  mirrored
                />

                {/* Camera overlay - forensic frame */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Main frame border */}
                  <div className="absolute inset-4 border-[3px] border-[#0e734b] rounded-sharper shadow-[0_0_20px_rgba(0,104,74,0.3)]" />

                  {/* Corner markers */}
                  <div className="absolute top-4 left-4 w-8 h-8 border-l-4 border-t-4 border-[#0e734b]" />
                  <div className="absolute top-4 right-4 w-8 h-8 border-r-4 border-t-4 border-[#0e734b]" />
                  <div className="absolute bottom-4 left-4 w-8 h-8 border-l-4 border-b-4 border-[#0e734b]" />
                  <div className="absolute bottom-4 right-4 w-8 h-8 border-r-4 border-b-4 border-[#0e734b]" />

                  {/* Focus indicator */}
                  <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-white/95 px-4 py-1.5 rounded-sharp uppercase tracking-[0.3em] text-[10px] text-[#0e734b] border-2 border-[#0e734b] font-bold shadow-lg">
                    Focus Area
                  </div>

                  {/* Bottom alignment guide */}
                  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-1/2 h-[3px] bg-gradient-to-r from-transparent via-[#0e734b] to-transparent rounded" />

                  {/* Scanline animation */}
                  <div className="forensic-scanline" />
                </div>

                {/* Countdown overlay */}
                {showCountdown && (
                  <div className="absolute bottom-20 left-1/2 -translate-x-1/2 pointer-events-auto">
                    <CountdownTimer onComplete={handleCountdownComplete} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center p-8 forensic-card max-w-md">
              <div className="forensic-marker mx-auto mb-4">!</div>
              <h2 className="text-2xl font-bold mb-3 text-[#1A1A1A]">Camera Not Available</h2>
              <p className="text-[#4A5550] mb-6 text-sm">
                Please enable camera access to continue with the photo capture.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="forensic-btn-primary px-8 py-3 text-sm flex items-center gap-2 mx-auto"
              >
                <span className="font-title uppercase">Retry</span>
                <span className="text-white/60">|</span>
                <span className="font-arabic font-bold">إعادة المحاولة</span>
              </button>
            </div>
          )}
        </main>

        {/* Footer status */}
        <footer className="px-8 pb-4">
          <div className="flex items-center justify-center gap-2 text-[#4A5550] text-[10px] uppercase tracking-[0.2em]">
            <div className="w-2 h-2 bg-[#0e734b] rounded-sm animate-pulse" />
            <span>Camera Active — Auto Capture Enabled</span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default CameraScreen;
